/**
 * Search Cache Utility for Personal Data
 *
 * Purpose: Provides caching and memoization for search operations
 *
 * Features:
 * - LRU cache for search results
 * - Debounced search optimization
 * - Search analytics tracking
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  hitCount: number;
}

type SearchCacheFilters = object;

export class SearchCache<T> {
  private cache = new Map<string, CacheEntry<T>>();
  private maxSize: number;
  private ttl: number; // Time to live in milliseconds

  constructor(maxSize = 50, ttlMinutes = 10) {
    this.maxSize = maxSize;
    this.ttl = ttlMinutes * 60 * 1000;
  }

  /**
   * Generate cache key from search parameters
   */
  private generateKey(searchTerm: string, filters: SearchCacheFilters): string {
    return `${searchTerm.toLowerCase()}_${JSON.stringify(filters)}`;
  }

  /**
   * Get cached result if available and not expired
   */
  get(searchTerm: string, filters: SearchCacheFilters): T | null {
    const key = this.generateKey(searchTerm, filters);
    const entry = this.cache.get(key);

    if (!entry) return null;

    // Check if entry is expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Update hit count for analytics
    entry.hitCount++;
    return entry.data;
  }

  /**
   * Store result in cache
   */
  set(searchTerm: string, filters: SearchCacheFilters, data: T): void {
    const key = this.generateKey(searchTerm, filters);

    // If cache is full, remove oldest entry (LRU)
    if (this.cache.size >= this.maxSize) {
      let oldestKey = '';
      let oldestTimestamp = Date.now();

      for (const [cacheKey, entry] of this.cache.entries()) {
        if (entry.timestamp < oldestTimestamp) {
          oldestTimestamp = entry.timestamp;
          oldestKey = cacheKey;
        }
      }

      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      hitCount: 0,
    });
  }

  /**
   * Clear all cached entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const entries = Array.from(this.cache.values());
    const totalHits = entries.reduce((sum, entry) => sum + entry.hitCount, 0);

    return {
      size: this.cache.size,
      totalHits,
      averageAge:
        entries.length > 0
          ? (Date.now() -
              entries.reduce((sum, entry) => sum + entry.timestamp, 0) /
                entries.length) /
            1000 /
            60
          : 0,
      hitRate: totalHits / (totalHits + this.cache.size), // Approximate hit rate
    };
  }
}

/**
 * Search Analytics Tracker
 */
export class SearchAnalytics {
  private static instance: SearchAnalytics;
  private searchHistory: Array<{
    term: string;
    timestamp: number;
    resultCount: number;
    filters: SearchCacheFilters;
  }> = [];
  private maxHistorySize = 100;

  static getInstance(): SearchAnalytics {
    if (!SearchAnalytics.instance) {
      SearchAnalytics.instance = new SearchAnalytics();
    }
    return SearchAnalytics.instance;
  }

  /**
   * Track a search event
   */
  trackSearch(
    term: string,
    resultCount: number,
    filters: SearchCacheFilters,
  ): void {
    this.searchHistory.push({
      term: term.toLowerCase(),
      timestamp: Date.now(),
      resultCount,
      filters,
    });

    // Keep history size manageable
    if (this.searchHistory.length > this.maxHistorySize) {
      this.searchHistory = this.searchHistory.slice(-this.maxHistorySize);
    }
  }

  /**
   * Get popular search terms
   */
  getPopularTerms(limit = 10): Array<{ term: string; count: number }> {
    const termCounts = new Map<string, number>();

    this.searchHistory.forEach((search) => {
      const count = termCounts.get(search.term) || 0;
      termCounts.set(search.term, count + 1);
    });

    return Array.from(termCounts.entries())
      .map(([term, count]) => ({ term, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Get search trends over time
   */
  getSearchTrends(days = 7): Array<{ date: string; searches: number }> {
    const now = Date.now();
    const dayInMs = 24 * 60 * 60 * 1000;
    const trends: Array<{ date: string; searches: number }> = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now - i * dayInMs);
      const dateStr = date.toISOString().split('T')[0] ?? '';
      const dayStart = date.setHours(0, 0, 0, 0);
      const dayEnd = dayStart + dayInMs;

      const searches = this.searchHistory.filter(
        (search) => search.timestamp >= dayStart && search.timestamp < dayEnd,
      ).length;

      trends.push({ date: dateStr, searches });
    }

    return trends;
  }

  /**
   * Get search analytics summary
   */
  getSummary() {
    const totalSearches = this.searchHistory.length;
    const avgResultsPerSearch =
      totalSearches > 0
        ? this.searchHistory.reduce(
            (sum, search) => sum + search.resultCount,
            0,
          ) / totalSearches
        : 0;

    return {
      totalSearches,
      avgResultsPerSearch,
      uniqueTerms: new Set(this.searchHistory.map((s) => s.term)).size,
      recentSearches: this.searchHistory.slice(-10).map((s) => s.term),
    };
  }
}

// Global cache instance for search results
export const searchCache = new SearchCache<unknown>();
